import * as React from 'react'
import { connect, useDispatch } from 'react-redux'
import { useTranslation, withTranslation, WithTranslation } from 'react-i18next'
import { checklistUpdateComponent } from '../../../../redux/actions'
import FormComponentBuilder, { FormComponentBuilderChildrenProps } from '../FormComponent/FormComponentBuilder'
import { useState } from 'react'

export interface ComponentData {
  id: string
  optional: boolean
  type: {
    type: string
    value: number
  }
  defaultValue: number
  lastValue: number
  title?: string | null
}

export interface StateProps {
  componentData: ComponentData
  checklistInstanceId: string
  checkId: string
}

export interface DispatchProps {
  updateComponent: (
    checklistInstanceId: string,
    componentId: string,
    value: number | null,
    conclude: boolean
  ) => Promise<any>
}

export interface OwnProps {
  collapseHandler?: Function
}

export interface OwnState {
  currentValue: number | null
  loading: boolean
}

export type Props = StateProps & DispatchProps & OwnProps & WithTranslation

const mapDispatchToProps = (dispatch: Function): DispatchProps => {
  return {
    updateComponent: (checklistInstanceId, componentId, value, conclude) =>
      dispatch(checklistUpdateComponent(checklistInstanceId, componentId, value, conclude)),
  }
}

export class NumberComponent extends React.Component<Props, OwnState> {
  constructor(props: Props) {
    super(props)
    const lastValue = props.componentData.lastValue
    const defaultValue = props.componentData.defaultValue
    this.state = {
      loading: false,
      currentValue: lastValue || defaultValue,
    }
    this.updateComponent = this.updateComponent.bind(this)
    this.manageCollapsable()
  }

  private manageCollapsable() {
    if (
      this.props.componentData.lastValue !== this.props.componentData.defaultValue &&
      this.props.componentData.optional &&
      this.props.collapseHandler
    ) {
      this.props.collapseHandler()
    }
  }

  private updateComponent(event: React.FocusEvent<HTMLInputElement>) {
    event.persist()
    this.props.updateComponent(
      this.props.checklistInstanceId,
      this.props.componentData.id,
      parseFloat(event.target.value),
      false
    )
  }

  public render() {
    return (
      <div className="collapse cl-controls-optional cl-controls-optional-1 show">
        <FormComponentBuilder<number> component={this.props.componentData} className="text-left">
          {props => {
            return (
              <>
                <label htmlFor={`${this.props.componentData.id}-value`}>
                  {this.props.componentData.title || this.props.t('checklist.components.number.value')}
                </label>
                <_Input
                  {...props}
                  checklistInstanceId={this.props.checklistInstanceId}
                  componentId={this.props.componentData.id}
                />
              </>
            )
          }}
        </FormComponentBuilder>
      </div>
    )
  }
}

function _Input(
  props: FormComponentBuilderChildrenProps<number> & { checklistInstanceId: string; componentId: string }
): JSX.Element {
  const [internalValue, setInternalValue] = useState<string>(props.value.toString())
  const dispatch = useDispatch()
  const { t } = useTranslation()

  React.useEffect(() => {
    setInternalValue(props.value.toString())
  }, [props.value])

  const updateComponent = React.useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    event.persist()
    const value = convertNumber(event.target.value)
    setInternalValue(value.toString())
    dispatch(checklistUpdateComponent(props.checklistInstanceId, props.componentId, value, false))
    props.onBlur()
  }, [])

  const onChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setInternalValue(event.target.value)
  }, [])

  const convertNumber = React.useCallback((value: string) => {
    const num = parseFloat(value)
    if (isNaN(num)) {
      return 0
    }
    return num
  }, [])

  return (
    <input
      type="number"
      min="0"
      step="0.01"
      id={`${props.componentId}-value`}
      className={`form-control ${props.className}`}
      placeholder={t('checklist.components.number.value')}
      onBlur={updateComponent}
      value={internalValue}
      onChange={onChange}
      disabled={props.disabled}
    />
  )
}

export default connect(null, mapDispatchToProps)(withTranslation()(NumberComponent))
